import { Color, Flex, Icon, Input, Spacing, Text } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'

import { FileListItem } from '../../types/fileListItem'
import * as Styled from './styles'

interface NameCellProps {
  isDraggable?: boolean
  isEditing: boolean
  item: FileListItem
  onDragEnd?: () => void
  onDragStart?: () => void
  onRename?: (id: string, newName: string) => void
  onStartEdit?: () => void
  onStopEdit: () => void
}

export const NameCell = ({
  isDraggable = false,
  isEditing,
  item,
  onDragEnd,
  onDragStart,
  onRename,
  onStartEdit,
  onStopEdit,
}: NameCellProps): ReactElement => {
  const { name, id, type } = item
  const [editName, setEditName] = useState(name)

  useEffect(() => {
    if (isEditing) {
      setEditName(name)
    }
  }, [isEditing, name])

  const handleSubmit = useCallback(() => {
    onRename?.(id, editName)
    onStopEdit()
  }, [editName, id, onRename, onStopEdit])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit()
      }
      if (e.key === 'Escape') {
        onStopEdit()
      }
    },
    [handleSubmit, onStopEdit],
  )

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', id)
      onDragStart?.()
    },
    [id, onDragStart],
  )

  const handleDragEnd = useCallback(() => {
    onDragEnd?.()
  }, [onDragEnd])

  if (isEditing) {
    return (
      <Input
        autoFocus
        onBlur={handleSubmit}
        onChange={(e) => setEditName(e.target.value)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        value={editName}
      />
    )
  }

  return (
    <Styled.NameCellWrapper
      draggable={isDraggable}
      onDragEnd={isDraggable ? handleDragEnd : undefined}
      onDragStart={isDraggable ? handleDragStart : undefined}
    >
      <Flex as="div" alignItems="center" gap={Spacing.S8}>
        <Styled.FolderIconWrapper isFolder={type === 'folder'}>
          <Icon
            icon={type === 'folder' ? 'folder' : 'file'}
            color={type === 'folder' ? Color.Lavender300 : Color.Grey800}
          />
        </Styled.FolderIconWrapper>
        <Styled.NameWrapper>
          <Text variant="bodyMD" color={Color.Black} onDoubleClick={onStartEdit}>
            {name}
          </Text>
        </Styled.NameWrapper>
      </Flex>
    </Styled.NameCellWrapper>
  )
}
